// client/src/hooks/usePlanCNudgeState.ts
//
// TRIL-116: aggregates Plan C nudge state — context presence,
// dismissal time-decay, and opt-out flag — plus write helpers
// (recordDismiss, optOut) that fire updateUserPreferences.

import { useMutation } from '@apollo/client';
import * as React from 'react';
import { UPDATE_USER_PREFERENCES_MUTATION } from '../graphql/resolvers/userMutations/updateUserPreferences.gql';
import { computeCompletionPct } from '../helpers/planCCompletion';
import { type NudgeState, shouldShowNudge } from '../helpers/planCDismissal';
import type { ProfessionalContext } from '../interfaces/user.interface';

interface Prefs {
  planCNudgeDismissCount?: number | null;
  planCNudgeDismissedAt?: string | null;
  planCOptedOut?: boolean | null;
}

interface Args {
  context: ProfessionalContext | null;
  prefs: Prefs;
  userId?: string;
}

interface ReturnShape {
  shouldShow: boolean;
  completionPct: number;
  recordDismiss: () => Promise<void>;
  optOut: () => Promise<void>;
}

export function usePlanCNudgeState({
  context,
  prefs,
  userId,
}: Args): ReturnShape {
  const [sessionDismissed, setSessionDismissed] = React.useState(false);
  const [updatePrefs] = useMutation(UPDATE_USER_PREFERENCES_MUTATION);

  const contextFilled = context != null;

  const nudgeState: NudgeState = {
    dismissedAt: prefs.planCNudgeDismissedAt ?? null,
    dismissCount: prefs.planCNudgeDismissCount ?? 0,
    optedOut: prefs.planCOptedOut ?? false,
  };

  const shouldShow =
    !contextFilled &&
    shouldShowNudge(nudgeState, Date.now(), {
      sessionDismissedThisLoad: sessionDismissed,
    });

  const completionPct = computeCompletionPct(context).pct;

  const recordDismiss = React.useCallback(async () => {
    setSessionDismissed(true);
    const nextCount = (prefs.planCNudgeDismissCount ?? 0) + 1;
    await updatePrefs({
      variables: {
        input: {
          userId,
          planCNudgeDismissCount: nextCount,
          planCNudgeDismissedAt: new Date().toISOString(),
        },
      },
    });
  }, [prefs.planCNudgeDismissCount, updatePrefs, userId]);

  const optOut = React.useCallback(async () => {
    await updatePrefs({
      variables: {
        input: { userId, planCOptedOut: true },
      },
    });
  }, [updatePrefs, userId]);

  return { shouldShow, completionPct, recordDismiss, optOut };
}
